import { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSearchLoading } from '@shared/hooks/useSearchLoading';
import { listContactRequestsApiService } from '../api/listContactRequestsApiService';
import type {
  ContactRequestListItem,
  ContactRequestStatus,
} from '../types/contactRequestTypes';
import type { FilterOption } from '@shared/components/FilterPopup';
import type { DateRange } from '@shared/components/FilterPopup/types';

const ALLOWED_LIMITS = [10, 30, 50, 100] as const;

const VALID_STATUSES: ContactRequestStatus[] = ['pending', 'completed'];

const DEFAULT_STATUS_OPTIONS: FilterOption[] = [
  { label: 'Otwarte', value: 'pending', checked: false },
  { label: 'Zakończone', value: 'completed', checked: false },
];

function sanitizeStatusString(
  statusStr: string | null | undefined
): string | undefined {
  if (!statusStr) {
    return undefined;
  }
  const trimmed = statusStr.trim();
  if (!trimmed) {
    return undefined;
  }
  const valid = trimmed
    .split(',')
    .map((s) => s.trim())
    .filter((s) => (VALID_STATUSES as string[]).includes(s));
  return valid.length > 0 ? valid.join(',') : undefined;
}

function statusListFromParam(statusStr: string | null | undefined): string[] {
  if (!statusStr) {
    return [];
  }
  return statusStr
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function buildStatusOptionsFromList(list: string[]): FilterOption[] {
  return DEFAULT_STATUS_OPTIONS.map((opt) => ({
    ...opt,
    checked: list.includes(opt.value),
  }));
}

export const useContactRequestList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isSyncingFromUrl = useRef(false);

  const urlPage = searchParams.get('page');
  const urlSearch = searchParams.get('search');
  const urlLimit = searchParams.get('limit');
  const urlStatus = sanitizeStatusString(searchParams.get('status'));
  const urlStartDate = searchParams.get('startDate') ?? undefined;
  const urlEndDate = searchParams.get('endDate') ?? undefined;

  const {
    search,
    debouncedSearch,
    setSearch: setSearchWithLoad,
    setSearchDirect: setSearchRaw,
    setLoading,
    effectiveLoading,
  } = useSearchLoading(
    (() => {
      const v = (urlSearch ?? '').trim();
      return v.length > 200 ? v.substring(0, 200) : v;
    })(),
    500
  );

  const [items, setItems] = useState<ContactRequestListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isNoFilterFetch, setIsNoFilterFetch] = useState(false);

  const [page, setPage] = useState(() => {
    const v = urlPage ? parseInt(urlPage, 10) : 1;
    return v < 1 ? 1 : v;
  });

  const [limit, setLimit] = useState(() => {
    const v = urlLimit ? parseInt(urlLimit, 10) : 10;
    return (ALLOWED_LIMITS as readonly number[]).includes(v) ? v : 10;
  });

  const [limitError, setLimitError] = useState('');

  const [statusOptions, setStatusOptions] = useState<FilterOption[]>(() =>
    urlStatus
      ? buildStatusOptionsFromList(statusListFromParam(urlStatus))
      : [...DEFAULT_STATUS_OPTIONS]
  );

  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: urlStartDate,
    endDate: urlEndDate,
  });

  const [appliedFilters, setAppliedFilters] = useState<{
    statusOptions: FilterOption[];
    dateRange: DateRange;
  }>(() => ({
    statusOptions: urlStatus
      ? buildStatusOptionsFromList(statusListFromParam(urlStatus))
      : [...DEFAULT_STATUS_OPTIONS],
    dateRange: { startDate: urlStartDate, endDate: urlEndDate },
  }));

  const prevSearchRef = useRef(search);
  const prevDebouncedSearchRef = useRef(debouncedSearch);

  // Sync state from URL
  useEffect(() => {
    isSyncingFromUrl.current = true;

    const currentPage = searchParams.get('page');
    const currentLimit = searchParams.get('limit');
    const currentSearch = searchParams.get('search');
    const currentStatus = sanitizeStatusString(searchParams.get('status'));
    const currentStartDate = searchParams.get('startDate') ?? undefined;
    const currentEndDate = searchParams.get('endDate') ?? undefined;

    const parsedPage = currentPage ? parseInt(currentPage, 10) : 1;
    if (parsedPage >= 1 && parsedPage !== page) {
      setPage(parsedPage);
    }

    const parsedLimit = currentLimit ? parseInt(currentLimit, 10) : 10;
    if (
      (ALLOWED_LIMITS as readonly number[]).includes(parsedLimit) &&
      parsedLimit !== limit
    ) {
      setLimit(parsedLimit);
    }

    const newSearch = (currentSearch ?? '').trim();
    if (newSearch !== search.trim()) {
      setSearchRaw(
        newSearch.length > 200 ? newSearch.substring(0, 200) : newSearch
      );
      setLoading(true);
    }

    const newStatusOpts = currentStatus
      ? buildStatusOptionsFromList(statusListFromParam(currentStatus))
      : [...DEFAULT_STATUS_OPTIONS];
    setStatusOptions(newStatusOpts);
    setAppliedFilters((prev) => {
      const statusSame =
        prev.statusOptions.length === newStatusOpts.length &&
        prev.statusOptions.every((o, i) => {
          const n = newStatusOpts[i];
          return (
            n !== undefined && o.value === n.value && o.checked === n.checked
          );
        });
      const dateSame =
        prev.dateRange.startDate === currentStartDate &&
        prev.dateRange.endDate === currentEndDate;
      if (statusSame && dateSame) {
        return prev;
      }
      return {
        statusOptions: newStatusOpts,
        dateRange: { startDate: currentStartDate, endDate: currentEndDate },
      };
    });
    setDateRange((prev) => {
      if (
        prev.startDate === currentStartDate &&
        prev.endDate === currentEndDate
      ) {
        return prev;
      }
      return { startDate: currentStartDate, endDate: currentEndDate };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (isSyncingFromUrl.current) {
      isSyncingFromUrl.current = false;
    }
  }, [page, limit, search, statusOptions, appliedFilters]);

  // Sync URL from state
  useEffect(() => {
    if (isSyncingFromUrl.current) {
      prevSearchRef.current = search;
      return;
    }

    const params = new URLSearchParams();
    const prevSearchTrimmed = prevSearchRef.current.trim();
    const currentSearchTrimmed = search.trim();
    const searchChanged = prevSearchTrimmed !== currentSearchTrimmed;

    if (searchChanged) {
      prevSearchRef.current = search;
      params.set('page', '1');
      if (page !== 1) {
        setPage(1);
      }
    } else {
      params.set('page', page.toString());
    }

    params.set('limit', limit.toString());
    if (search) {
      params.set('search', search);
    }

    const selectedStatuses = appliedFilters.statusOptions
      .filter((o) => o.checked)
      .map((o) => o.value)
      .join(',');
    if (selectedStatuses) {
      params.set('status', selectedStatuses);
    }

    if (appliedFilters.dateRange.startDate) {
      params.set('startDate', appliedFilters.dateRange.startDate);
    }
    if (appliedFilters.dateRange.endDate) {
      params.set('endDate', appliedFilters.dateRange.endDate);
    }

    const cols = searchParams.get('cols');
    if (cols) {
      params.set('cols', cols);
    }

    const tab = searchParams.get('tab');
    if (tab) {
      params.set('tab', tab);
    }

    if (
      params.toString() !==
      new URLSearchParams(searchParams.toString()).toString()
    ) {
      setSearchParams(params, { replace: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, search, appliedFilters, setSearchParams]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const selectedStatuses = appliedFilters.statusOptions
        .filter((o) => o.checked)
        .map((o) => o.value)
        .join(',');

      const response = await listContactRequestsApiService.listContactRequests({
        page,
        limit,
        ...(debouncedSearch.trim() && { search: debouncedSearch.trim() }),
        ...(selectedStatuses && { status: selectedStatuses }),
        ...(appliedFilters.dateRange.startDate && {
          startDate: appliedFilters.dateRange.startDate,
        }),
        ...(appliedFilters.dateRange.endDate && {
          endDate: appliedFilters.dateRange.endDate,
        }),
      });

      if (
        response.pagination.totalCount > 0 &&
        response.pagination.totalPages > 0 &&
        page > response.pagination.totalPages
      ) {
        setPage(response.pagination.totalPages);
        return;
      }

      setItems(response.data);
      setTotalCount(response.pagination.totalCount);

      const hasNoFilters =
        !debouncedSearch.trim() &&
        !selectedStatuses &&
        !appliedFilters.dateRange.startDate &&
        !appliedFilters.dateRange.endDate;
      setIsNoFilterFetch(hasNoFilters);
    } catch {
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, limit, debouncedSearch, appliedFilters, setLoading]);

  useEffect(() => {
    prevDebouncedSearchRef.current = debouncedSearch;
  }, [debouncedSearch]);

  useEffect(() => {
    const prev = prevDebouncedSearchRef.current.trim();
    const curr = debouncedSearch.trim();
    if (prev !== curr && page !== 1) {
      setPage(1);
    }
  }, [debouncedSearch, page]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const setSearch = useCallback(
    (newSearch: string) => {
      if (newSearch.length > 200) {
        return;
      }
      const currentTrimmed = search.trim();
      const newTrimmed = newSearch.trim();
      if (!isSyncingFromUrl.current && currentTrimmed !== newTrimmed) {
        setPage(1);
      }
      setSearchWithLoad(newSearch);
    },
    [search, setSearchWithLoad]
  );

  const handleLimitChange = useCallback((newLimit: number) => {
    if (!(ALLOWED_LIMITS as readonly number[]).includes(newLimit)) {
      setLimitError('Dozwolone wartości: 10, 30, 50, 100.');
      return;
    }
    setLimitError('');
    setLimit(newLimit);
    setPage(1);
  }, []);

  const handleApplyFilters = useCallback(() => {
    setAppliedFilters({ statusOptions, dateRange });
    setPage(1);
  }, [statusOptions, dateRange]);

  const handleClearFilters = useCallback(() => {
    const hasAppliedFilters =
      appliedFilters.statusOptions.some((o) => o.checked) ||
      !!appliedFilters.dateRange.startDate ||
      !!appliedFilters.dateRange.endDate;

    setStatusOptions([...DEFAULT_STATUS_OPTIONS]);
    setDateRange({});

    if (hasAppliedFilters) {
      setAppliedFilters({
        statusOptions: [...DEFAULT_STATUS_OPTIONS],
        dateRange: {},
      });
      setPage(1);
    }
  }, [appliedFilters]);

  const isSearchClearing =
    !search.trim() &&
    debouncedSearch.trim() !== '' &&
    !appliedFilters.statusOptions.some((o) => o.checked);

  const prevDebouncedTrimmed = prevDebouncedSearchRef.current.trim();
  const currentDebouncedTrimmed = debouncedSearch.trim();
  const justClearedSearch =
    prevDebouncedTrimmed !== '' &&
    currentDebouncedTrimmed === '' &&
    !appliedFilters.statusOptions.some((o) => o.checked);

  return {
    items,
    loading: effectiveLoading,
    page,
    setPage,
    limit,
    totalCount,
    isNoFilterFetch,
    search,
    setSearch,
    isSearchClearing: isSearchClearing || justClearedSearch,
    statusOptions,
    setStatusOptions,
    dateRange,
    setDateRange,
    appliedFilters,
    handleApplyFilters,
    handleClearFilters,
    limitError,
    handleLimitChange,
    refetch: fetchItems,
  };
};
